import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  FETCH_ALL_MERCHANT_VALIDATIONS,
  FETCH_ALL_POSTPAID_MERCHANT,
  FETCH_INVOICE,
  MARK_INVOICE_PAID,
} from "../../../utils/constant";
import useApiServices from "../../../utils/useApiServices";
import swal from "sweetalert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CustomTip from "../../../common/CustomToolTip/CustomTip";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { ToolsActionRegistry } from "../../../Privillages/ActionRegistry";
import useHasPrivilege from "../../../Privillages/useHasPrivilege";

const isFutureDueDate = (dueDateString) => {
  if (!dueDateString) return false;
  const dueDate = new Date(dueDateString.replace(" ", "T"));
  return dueDate > new Date(); // true only if greater than current local time
};

async function generateInvoicePreview(data) {
  // Canvas-based preview generation using browser APIs only
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  // A4 dimensions in pixels at 72 DPI (points)
  const pageWidth = 595;
  const pageHeight = 842;
  const scale = 2; // For sharper image
  canvas.width = pageWidth * scale;
  canvas.height = pageHeight * scale;
  ctx.scale(scale, scale);

  // White background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, pageWidth, pageHeight);

  // Border
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 1;
  ctx.strokeRect(20, 20, pageWidth - 40, pageHeight - 40);

  let y = 80;
  const margin = 40;
  const centerX = pageWidth / 2;
  const valueX = 280; // Adjusted for better alignment

  // Title
  ctx.font = "bold 18px Arial";
  ctx.fillStyle = "#000000";
  const title = "MERCHANT INVOICE";
  const titleMetrics = ctx.measureText(title);
  ctx.fillText(title, centerX - titleMetrics.width / 2, y);

  y += 60;

  // Details
  ctx.font = "normal 10px Arial";
  const details = [
    { label: "MID:", value: data.mid },
    { label: "Company Name:", value: data.companyName },
    { label: "Billing Cycle:", value: data.billingCycle },
    { label: "Start Date:", value: data.start },
    { label: "End Date:", value: data.end },
    { label: "Invoice Due Date:", value: data.invoiceDueDate },
    {
      label: "Total Transaction Amount:",
      value: data.totalTransactionAmount?.toLocaleString() || "0",
    },
  ];

  details.forEach((item) => {
    ctx.fillText(item.label, margin, y);
    ctx.fillText(String(item.value ?? ""), valueX, y);
    y += 18;
  });

  y += 20;

  // Charges
  const charges = [
    {
      label: "Service Charge:",
      value: `INR ${data.serviceCharge?.toFixed(2) || "0.00"}`,
    },
    {
      label: "GST (18%):",
      value: `INR ${data.gstOnServiceCharge?.toFixed(2) || "0.00"}`,
    },
  ];

  charges.forEach((item) => {
    ctx.fillText(item.label, margin, y);
    ctx.fillText(item.value, valueX, y);
    y += 18;
  });

  y += 10;

  // Separator line
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(margin, y);
  ctx.lineTo(margin + 300, y);
  ctx.stroke();

  y += 20;

  // Total
  ctx.font = "bold 12px Arial";
  ctx.fillText("TOTAL AMOUNT TO BE PAID", margin, y);
  ctx.fillText(
    `INR ${data.totalAmountToBePaid?.toFixed(2) || "0.00"}`,
    valueX,
    y
  );

  y += 10;

  // Bottom separator
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  ctx.moveTo(margin, y);
  ctx.lineTo(margin + 300, y);
  ctx.stroke();

  // Convert canvas to blob
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        resolve(null);
        return;
      }
      const url = URL.createObjectURL(blob);
      resolve({ url, blob });
    }, "image/png");
  });
}

const BillingDetails = ({ toggleRightBar }) => {
  const { TOOLS_OTHERS, BILLING_EDIT } = ToolsActionRegistry;
  const hasBillingEdit = useHasPrivilege(TOOLS_OTHERS, BILLING_EDIT);
  const [merchantList, setMerchantList] = useState([]);
  const [selectedMerchant, setSelectedMerchant] = useState("");
  const [loading, setLoading] = useState(false);
  const [invoiceList, setInvoiceList] = useState([]);
  const [error, setError] = useState("");

  const { apiCallFnforGet, apiCallFnforPut } = useApiServices();

  const getMerchantData = async () => {
    try {
      setLoading(true);
      const response = await apiCallFnforGet(FETCH_ALL_POSTPAID_MERCHANT);
      if (response?.statusCode === 200) {
        setMerchantList(response?.data || []);
      } else {
        setMerchantList([]);
      }
    } catch (error) {
      console.error("Failed to fetch merchants:", error);
      setMerchantList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMerchantData();
  }, []);

  const fetchInvoicesByMerchant = async (mid) => {
    try {
      setLoading(true);
      setInvoiceList([]);
      setError("");

      const res = await apiCallFnforGet(FETCH_INVOICE + mid);
      console.log(res, "res!");
      if (res?.status !== "200") {
        throw new Error(res?.message || "Failed to fetch invoice data");
      }

      setInvoiceList(res.data || []);
    } catch (err) {
      setError(err.message);
      swal("Error!", err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedMerchant?.mid) {
      fetchInvoicesByMerchant(selectedMerchant.mid);
    } else {
      setInvoiceList([]);
    }
  }, [selectedMerchant]);

  const handleMerchantChange = (e) => {
    const mid = e.target.value;
    const merchant = merchantList.find((m) => m.mid === mid);
    if (!merchant) return swal("Alert!", "Merchant not found!", "error");

    setSelectedMerchant(merchant);
    setError("");
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      setLoading(true);

      const data = {
        mid: invoice.mid,
        companyName: invoice.companyName,
        billingCycle: invoice.billingCycle,
        start: invoice.start,
        end: invoice.end,
        invoiceDueDate: invoice.invoiceDueDate,
        totalTransactionAmount: invoice.totalTransactionAmount,
        gstOnServiceCharge: invoice.gstOnServiceCharge,
        serviceCharge: invoice.serviceCharge,
        totalAmountToBePaid: invoice.totalAmountToBePaid,
      };

      const result = await generateInvoicePreview(data);
      if (result && result.url) {
        const link = document.createElement("a");
        link.href = result.url;
        link.download = `invoice-${invoice.invoiceNumber || "document"}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // optional cleanup
        // URL.revokeObjectURL(result.url);
      }
    } catch (err) {
      console.error("Error downloading invoice:", err);
      swal("Error!", "Failed to download invoice", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = (invoice) => {
    const preview = {
      mid: invoice.mid,
      companyName: invoice.companyName,
      billingCycle: invoice.billingCycle,
      start: invoice.start,
      end: invoice.end,
      invoiceDueDate: invoice.invoiceDueDate,
      totalTransactionAmount: invoice.totalTransactionAmount,
      serviceCharge: invoice.serviceCharge,
      gstOnServiceCharge: invoice.gstOnServiceCharge,
      totalAmountToBePaid: invoice.totalAmountToBePaid,
    };

    generateInvoicePreview(preview).then((res) => {
      if (res?.url) window.open(res.url, "_blank");
    });
  };

  const handleMarkPaid = async (invoice) => {
    if (!invoice?.invoiceNumber || !invoice?.mid) {
      swal("Error!", "Invalid invoice data!", "error");
      return;
    }

    const confirm = await swal({
      title: "Are you sure?",
      text: `Mark invoice ${invoice.invoiceNumber} as PAID?`,
      icon: "warning",
      buttons: true,
      dangerMode: false,
    });

    if (!confirm) return;

    const payload = {
      invoiceNumber: invoice.invoiceNumber,
      mid: invoice.mid,
    };

    try {
      setLoading(true);
      const res = await apiCallFnforPut(MARK_INVOICE_PAID, payload);
      console.log(res, "res!");
      if (res?.statusCode === 200) {
        swal("Success!", "Invoice marked paid successfully ✅", "success");
        fetchInvoicesByMerchant(selectedMerchant?.mid);
      } else {
        swal(
          "Error!",
          res?.message || "Failed to update invoice status",
          "error"
        );
      }
    } catch (err) {
      console.error(err);
      swal("Error!", "Something went wrong while updating invoice!", "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { headerName: "Invoice No.", field: "invoiceNumber", width: 220 },
    { headerName: "Company", field: "companyName", width: 260 },
    { headerName: "Billing Cycle", field: "billingCycle", width: 160 },
    { headerName: "Status", field: "status", width: 140 },
    {
      headerName: "Total Txn Amt",
      field: "totalTransactionAmount",
      width: 180,
      renderCell: (params) => `₹ ${params.value.toLocaleString()}`,
    },
    { headerName: "Due Date", field: "invoiceDueDate", width: 200 },
    {
      headerName: "Action",
      field: "action",
      width: 220,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const inv = params.row;
        return (
          <div className="d-flex justify-content-start align-items-center gap-4">
            <CustomTip size="18" placement="top" title="View Invoice">
              <div
                className="d-inline-block"
                onClick={() => handleViewInvoice(inv)}
                style={{ cursor: "pointer" }}
              >
                <VisibilityIcon fontSize="medium" />
              </div>
            </CustomTip>

            <CustomTip size="18" placement="top" title="Download Invoice">
              <div
                className="d-inline-block"
                onClick={() => handleDownloadInvoice(inv)}
                style={{ cursor: "pointer" }}
              >
                <FileDownloadIcon fontSize="medium" />
              </div>
            </CustomTip>

            {isFutureDueDate(inv?.invoiceDueDate) && hasBillingEdit ? (
              <CustomTip size="18" placement="top" title="Mark Paid">
                <div
                  className="d-inline-block"
                  onClick={() => handleMarkPaid(inv)}
                  style={{ cursor: "pointer" }}
                >
                  <CheckCircleIcon fontSize="medium" className="text-success" />
                </div>
              </CustomTip>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div className="justify-content-between bg-white">
      {/* Header Section */}
      <div className="d-flex justify-content-start align-items-center">
        <button
          type="button"
          className="btn-close m-3"
          onClick={toggleRightBar}
        ></button>
        <div className="fs-2 text-dark fw-bold">Billing Details</div>
      </div>
      <div className="p-3">
        <Row className="mb-4">
          <Col md={6} className="mx-auto">
            <label className="fs-4 text-dark mb-1">Select Merchant</label>
            <select
              className="form-control"
              onChange={handleMerchantChange}
              value={selectedMerchant?.mid || ""}
              disabled={loading}
            >
              <option value="">
                {loading ? "Loading..." : "Select Merchant"}
              </option>
              {merchantList?.map((m) => (
                <option key={m?.mid} value={m?.mid}>
                  {m?.companyName} — {m?.mid}
                </option>
              ))}
            </select>
          </Col>
        </Row>
        <div className="">
          {/* Invoice List */}
          {!loading && invoiceList.length > 0 && (
            <div className="mt-3">
              <DataGrid
                rows={invoiceList}
                columns={columns.map((column) => ({
                  ...column,
                  renderCell: (params) =>
                    column.field === "action"
                      ? column.renderCell(params)
                      : params.value,
                }))}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: { showQuickFilter: true },
                }}
                getRowId={(row) => row?.id}
                autoHeight
                hideFooter
              />
            </div>
          )}

          {!loading && invoiceList.length === 0 && selectedMerchant && (
            <div className="text-center text-secondary fs-5">
              No invoices found for this merchant.
            </div>
          )}

          {error && (
            <div className="text-danger text-center fs-5 mt-2">{error}</div>
          )}
          {error && (
            <div className="col-sm-12 col-md-6 offset-md-3 mt-2">
              <div className="text-danger">{error}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingDetails;
